import { orchestrator } from 'satcheljs';
import { updateTimeScaleSetting } from '../actions/updateTimeScaleSetting';
import { updateSurfaceOptions } from 'owa-calendar-options-actions';
import type { CalendarSurfaceOptions } from 'owa-outlook-service-option-store';
import { lazyCreateOrUpdateOptionsForFeature } from 'owa-outlook-service-options';
import { getOptionsForFeature, OwsOptionsFeatureType } from 'owa-outlook-service-option-store';
import { logUsage } from 'owa-analytics';
import type { TimeScaleSetting, TimeScaleSource } from 'owa-calendar-timescale';
import {
    type UTCalendarFeatureName,
    type UTCalendarFeatureAction,
    type UTCalendarOrigin,
    type UTCalendarUIContext,
} from 'owa-unified-telemetry';
import { calendarDateRangeTypeMap } from 'owa-calendar-unified-telemetry-utils/lib/utils/surfacePropertyMappings';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

const timeScaleSettingToUTFeatureAction: Record<TimeScaleSetting, UTCalendarFeatureAction> = {
    [60]: 'SixtyMinutes',
    [30]: 'ThirtyMinutes',
    [15]: 'FifteenMinutes',
    [10]: 'TenMinutes',
    [6]: 'SixMinutes',
    [5]: 'FiveMinutes',
};

const timeScaleSourceToUIContextAndOrigin: Record<
    TimeScaleSource,
    {
        FeatureUIContext: UTCalendarUIContext;
        Origin?: UTCalendarOrigin;
    }
> = {
    Ribbon: { FeatureUIContext: 'AppRibbon', Origin: 'ViewTab' },
    Toolbar: { FeatureUIContext: 'NavigationBar' },
    Context_Menu: {
        FeatureUIContext: 'CalendarSurface',
        Origin: 'SurfaceContextMenu',
    },
    Settings: { FeatureUIContext: 'Other' },
    Import_Settings: { FeatureUIContext: 'Other' },
};

/* eslint-disable-next-line owa-custom-rules/forbid-orchestrator-in-same-package -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!
 *	> Do not register an orchestrator with an action that is defined in the same package */
orchestrator(updateTimeScaleSetting, async actionMessage => {
    const { newValue, source, dateRangeType } = actionMessage;
    const userOptions = getOptionsForFeature<CalendarSurfaceOptions>(
        OwsOptionsFeatureType.CalendarSurfaceOptions
    );

    let actionSuccess = true;

    try {
        await lazyCreateOrUpdateOptionsForFeature.importAndExecute(
            OwsOptionsFeatureType.CalendarSurfaceOptions,
            {
                ...userOptions,
                timeScaleSetting: newValue,
            } as CalendarSurfaceOptions
        );
    } catch {
        actionSuccess = false;
    }

    const mailboxInfo = getModuleContextMailboxInfo();
    logUsage('TimeScaleSettingUpdated', mailboxInfo, {
        timeScale: newValue,
        source,
        dateRangeType,
    });
    if (source != 'Import_Settings') {
        const { FeatureUIContext, Origin } = timeScaleSourceToUIContextAndOrigin[source];

        const utData = {
            FeatureName: 'SetTimeScale',
            FeatureAction: timeScaleSettingToUTFeatureAction[newValue as TimeScaleSetting],
            FeatureUIContext,
            Origin,
            CalendarViewType: dateRangeType ? calendarDateRangeTypeMap[dateRangeType] : undefined,
            ActionSuccess: actionSuccess,
        };

        logUsage('CalendarSurfaceUserAction', mailboxInfo, utData as any, {
            unifiedTelemetry: {
                eventName: 'CalendarSurfaceUserAction',
                bypassEventMatchingCheck: true,
                data: utData,
            },
            isScorecardEvent: true,
        });
    }
    updateSurfaceOptions();
});
